import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';

import { CARD_ELEMENT_TYPE } from '../../../../../../../services/cards/constants';
import Label from '../../../../../Label';
import Button from '../../../../../Button';
import {
  FONT_FAMILY,
  TEXT_ALIGN,
  COMMON_PRESETS,
  TEXT_PRESETS,
  googleFontInfo,
} from '../../../CardDesignerCardElements/CardDesignerCardElementText/helpers';
import InfoCircleIcon from '../../../../../../icons/InfoCircleIcon';
import GoogleFontLoader from '../../../../../../../services/cards/components/CardElementText/GoogleFontLoader';
import Angle from '../../../../../Angle';

const propTypes = {
  className: PropTypes.string,
  onCardElementCreate: PropTypes.func.isRequired,
  zElementIndexMax: PropTypes.number,
  lastUsedFont: PropTypes.oneOf(Object.values(FONT_FAMILY)),
};

const CardDesignerTextControls = ({
  className,
  onCardElementCreate,
  zElementIndexMax,
  lastUsedFont,
}) => {
  const [showCommonPresets, setShowCommonPresets] = useState(false);
  const [showTextPresets, setShowTextPresets] = useState(false);
  const classes = classNames({
    [className]: !!className,
  });

  const googleFontDetails = googleFontInfo.find(x => x.font === lastUsedFont);

  const commonBoxes = useMemo(
    () =>
      Object.values(COMMON_PRESETS).map(preset => ({
        label: preset,
        font_size: '16px',
        cardElementConfiguration: {
          help_placeholder: preset,
          is_editable: true,
          text_align: TEXT_ALIGN.CENTER,
          font_size: 0.08,
        },
        input: {
          frame: {
            x: 0.1,
            y: 0.25,
            width: 0.8,
            height: 0.05,
          },
        },
      })),
    [],
  );

  const textBoxes = useMemo(
    () =>
      Object.values(TEXT_PRESETS).map(preset => ({
        label: preset,
        font_size: '16px',
        cardElementConfiguration: {
          help_placeholder: preset,
          is_editable: true,
          text_align: TEXT_ALIGN.CENTER,
          font_size: 0.08,
        },
        input: {
          frame: {
            x: 0.1,
            y: 0.25,
            width: 0.8,
            height: 0.05,
          },
        },
      })),
    [],
  );

  return (
    <div className={classes}>
      <div className="card-designer-layout-text-controls">
        <Label id="text-control">Add text</Label>
        <Button
          btnBlock
          className="mb-4"
          onClick={() => {
            onCardElementCreate({
              cardElementConfiguration: {
                is_editable: true,
                font_family: lastUsedFont,
              },
              cardElementType: CARD_ELEMENT_TYPE.TEXT,
              input: {
                frame: {
                  x: 0.08,
                  y: 0.08,
                  width: 0.44,
                  height: 0.06,
                },
                z_index: zElementIndexMax ?? 2,
              },
            });
          }}
        >
          Add a text box
        </Button>
        <Button
          id="text-presets-control"
          btnStyle="link"
          btnSize="sm"
          className="text-light btn-preset"
          onClick={() => setShowCommonPresets(prev => !prev)}
        >
          COMMONLY USED{' '}
          <Angle
            direction={showCommonPresets ? 'up' : 'down'}
            className="ml-1"
          />
          <InfoCircleIcon
            size="12"
            data-tip=""
            data-for="common-preset-tip"
            data-event="mouseenter click"
            data-effect="solid"
            data-place="bottom"
            className="ml-1"
          />
          <ReactTooltip id="common-preset-tip" className="tooltip">
            Fields commonly used on custom prints. A helper text is preset to
            guide in filling the field while configuring their Custom Print.
          </ReactTooltip>
        </Button>
        {showCommonPresets && (
          <div className="mt-1">
            {commonBoxes.map(textBox => {
              return (
                <Button
                  btnStyle="reset"
                  className="card-designer-layout-text-controls-button"
                  onClick={() => {
                    onCardElementCreate({
                      cardElementConfiguration: {
                        is_editable: true,
                        ...textBox.cardElementConfiguration,
                      },
                      cardElementType: CARD_ELEMENT_TYPE.TEXT,
                      input: {
                        ...textBox.input,
                        z_index: zElementIndexMax ?? 2,
                      },
                    });
                  }}
                  style={{
                    fontFamily:
                      googleFontDetails?.fontFamily ??
                      textBox.cardElementConfiguration.font_family,
                    fontWeight: textBox.cardElementConfiguration.font_weight,
                    textTransform:
                      textBox.cardElementConfiguration.text_transform,
                    fontSize: textBox.font_size,
                  }}
                >
                  {textBox.label}
                </Button>
              );
            })}
          </div>
        )}
        <hr className="my-1" />
        <Button
          id="text-presets-control"
          btnStyle="link"
          btnSize="sm"
          className="text-light btn-preset"
          onClick={() => setShowTextPresets(prev => !prev)}
        >
          TEXT PRESETS{' '}
          <Angle direction={showTextPresets ? 'up' : 'down'} className="ml-1" />
          <InfoCircleIcon
            size="12"
            data-tip=""
            data-for="text-preset-tip"
            data-event="mouseenter click"
            data-effect="solid"
            data-place="bottom"
            className="ml-1"
          />
          <ReactTooltip id="text-preset-tip" className="tooltip">
            Pre-sets are automatically filled with the appropriate data when the
            Custom Prints is configured by a purchaser. If matching data is not
            found, the preset is cleared and hidden.
          </ReactTooltip>
        </Button>
        {showTextPresets && (
          <div className="mt-1">
            {textBoxes.map(textBox => {
              return (
                <Button
                  btnStyle="reset"
                  className="card-designer-layout-text-controls-button"
                  onClick={() => {
                    onCardElementCreate({
                      cardElementConfiguration: {
                        is_editable: true,
                        ...textBox.cardElementConfiguration,
                      },
                      cardElementType: CARD_ELEMENT_TYPE.TEXT,
                      input: {
                        ...textBox.input,
                        z_index: zElementIndexMax ?? 2,
                      },
                    });
                  }}
                  style={{
                    fontFamily:
                      googleFontDetails?.fontFamily ??
                      textBox.cardElementConfiguration.font_family,
                    fontWeight: textBox.cardElementConfiguration.font_weight,
                    textTransform:
                      textBox.cardElementConfiguration.text_transform,
                    fontSize: textBox.font_size,
                  }}
                >
                  {textBox.label}
                </Button>
              );
            })}
          </div>
        )}

        {googleFontDetails && <GoogleFontLoader fontFamily={lastUsedFont} />}
      </div>
    </div>
  );
};

CardDesignerTextControls.propTypes = propTypes;

export default CardDesignerTextControls;
